import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import { Result } from 'ts-results'

import { useNotifications } from 'app/application/application'
import { Form } from 'app/common/presentation'
import {
  AmazonIcon,
  DV360Icon,
  FacebookIcon,
  GoogleAdsenseIcon,
  GoogleAnalyticsIcon,
} from 'app/common/presentation/icons'
import {
  AvailableConnection,
  ConnectionAuthConfig,
  isInstalledConnection,
  isOAuthUser,
} from 'app/connections/types'

import { Button, Link } from 'components'

import AmazonLogin from './AuthorizationLogin/AmazonLogin'
import CriteoLogin from './AuthorizationLogin/CriteoLogin'
import FacebookLogin from './AuthorizationLogin/FacebookLogin'
import GoogleLogin from './AuthorizationLogin/GoogleLogin'
import TiktokLogin from './AuthorizationLogin/TiktokLogin'
import { AuthorizationState } from './GenericSettings'
import { AuthorizationData } from './Settings'
import styles from './Setup.module.scss'

const getAuthorizationCode = (
  authConfig: ConnectionAuthConfig,
  redirectUri: string,
  handleAuthorizationSuccess: (authorizationData: AuthorizationData) => unknown,
  handleAuthorizationError: (errorMessage: string | null) => void,
  title: string,
) => {
  const urlString = new URLSearchParams(authConfig.params)
  const url = `${authConfig.url}?redirect_uri=${redirectUri}&${urlString}`

  const authWindow = window.open(
    url,
    `${title} Authorization`,
    `width=500,height=600,left=50,top=50`,
  )

  if (authWindow) {
    const pollTimer = window.setInterval(() => {
      try {
        // Check if the popup is closed without authorization redirect (eg by user)
        if (!authWindow || authWindow.closed) {
          clearInterval(pollTimer) // Stop polling
          return
        }

        const urlParams = new URLSearchParams(authWindow.location.search)
        if (redirectUri.endsWith(authWindow.location.pathname)) {
          if (urlParams.has('code')) {
            window.clearInterval(pollTimer)
            handleAuthorizationSuccess({
              authorizationCode: urlParams.get('code') ?? undefined,
              redirectUri,
            })
            authWindow.close()
          }
          if (urlParams.has('error') || urlParams.has('error_message')) {
            window.clearInterval(pollTimer)
            handleAuthorizationError(urlParams.get('error') ?? urlParams.get('error_message'))
            authWindow.close()
          }
        }
      } catch (_) {
        // ignore as authWindow.location will throw a cors error, until the authorization redirect happend
      }
    }, 500)
  }
}

const renderLoginButton = (connection: AvailableConnection) => {
  switch (connection.name) {
    case 'amazon':
      return <AmazonLogin />
    case 'criteo':
      return <CriteoLogin />
    case 'facebook':
      return <FacebookLogin />
    case 'dv360':
    case 'googleAdwords':
    case 'googleAnalytics':
      return <GoogleLogin />
    case 'tiktok':
      return <TiktokLogin />
  }
}

interface AuthorizationProps {
  authorizationState: AuthorizationState
  redirectUri?: string
  setAuthorizationData?: (
    authorizationData: AuthorizationData,
  ) => Promise<Result<void, string | void>>
  connection: AvailableConnection
}

const Authorization = ({
  authorizationState,
  redirectUri,
  setAuthorizationData,
  connection,
}: AuthorizationProps) => {
  const { t } = useTranslation('connections')
  const { createNotification } = useNotifications()

  const [authorizationError, setAuthorizationError] = useState<string>()

  const handleAuthorizationError = (
    authorizationErrorMessage: string | null,
    withNotification = true,
  ) => {
    const translationKey = `setup.authorization.error.${authorizationErrorMessage}`
    const translatedErrorMessage = t(translationKey)
    const errorMessage =
      translationKey === translatedErrorMessage
        ? `Authorization error: ${authorizationErrorMessage}`
        : translatedErrorMessage

    if (withNotification) {
      createNotification({
        message: errorMessage,
        title: 'Authorization error',
        type: 'error',
      })
    }

    setAuthorizationError(errorMessage)
  }

  const handleAuthorizationSuccess = (authorizationData: AuthorizationData) => {
    if (setAuthorizationData) {
      void setAuthorizationData(authorizationData).then((result) => {
        if (result.err && result.val) {
          handleAuthorizationError(result.val, false)
        }
      })
    }
  }

  const handleAuthorization = (authConfig: ConnectionAuthConfig, redirectUri: string) =>
    getAuthorizationCode(
      authConfig,
      redirectUri,
      handleAuthorizationSuccess,
      handleAuthorizationError,
      t(`available.${connection.name}.displayName`),
    )

  const authorizationUser =
    isInstalledConnection(connection) && isOAuthUser(connection.authorizationUser)
      ? connection.authorizationUser
      : null

  const formatTimestamp = (timestamp: string | null) => {
    if (timestamp) {
      const timestampAsDate = new Date(timestamp)
      if (new Date().getTime() - timestampAsDate.getTime() < 60000) {
        return t('setup.authorization.oauth.aFewSeconds')
      } else {
        return t('setup.authorization.oauth.on', {
          date: format(timestamp, 'MMM dd, HH:mm:ss aa'),
        })
      }
    }

    return ''
  }

  const getDescription = () => {
    if (authorizationUser) {
      return authorizationUser.email
        ? t('setup.authorization.oauth.addedBy', {
            email: authorizationUser.email,
            when: formatTimestamp(authorizationUser.timestamp),
          })
        : ''
    }
  }

  const getIcon = () => {
    switch (connection.name) {
      case 'amazon':
        return AmazonIcon
      case 'dv360':
        return DV360Icon
      case 'facebook':
        return FacebookIcon
      case 'googleAdwords':
        return GoogleAdsenseIcon
      case 'googleAnalytics':
        return GoogleAnalyticsIcon
    }

    return undefined
  }

  const getTitle = () => {
    if (authorizationUser) {
      return authorizationUser.name ?? ''
    }

    return t(`setup.authorization.${connection.name}.title`)
  }

  const renderAuthorizationState = () => {
    if (connection.authConfig && redirectUri && setAuthorizationData) {
      const requestAuthorization = () => handleAuthorization(connection.authConfig!, redirectUri)

      switch (authorizationState) {
        case 'OUTDATED':
        case 'OUTDATED_OR_VALID':
          return authorizationUser ? (
            <Button onClick={requestAuthorization} variant="secondary">
              {t('setup.authorization.oauth.reauthorize')}
            </Button>
          ) : (
            <Link
              title={t(`setup.authorization.${connection.name}.action`)}
              to={requestAuthorization}
            ></Link>
          )
        case 'PENDING':
          return <button onClick={requestAuthorization}>{renderLoginButton(connection)}</button>
        case 'VALID':
          return (
            <Button onClick={requestAuthorization} variant="secondary">
              {t('setup.authorization.oauth.reauthorize')}
            </Button>
          )
      }
    }
  }

  return (
    <>
      <Form.FormRow
        className={styles.authorization}
        description={getDescription()}
        icon={getIcon()}
        title={getTitle()}
      >
        <div className={styles.state}>
          {renderAuthorizationState()}
          {authorizationError && <div className={styles.error}>{authorizationError}</div>}
        </div>
      </Form.FormRow>
    </>
  )
}

export default Authorization
